import {useMutation, useQueryClient} from '@github-ui/react-query'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {
  fetchWithErrorHandling,
  parseJSONWithBetterErrors,
  throwErrorsIfBadResponse,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import {useMergeBoxQueryKey} from '@github-ui/mergebox/use-merge-box-query-key'
import {useInvalidatePrState} from '../../../hooks/use-invalidate-pr-state'
import {useLayoutQuery} from '../../../page-data/loaders/use-layout-query'
import {timelineQueryKey} from '../../../page-data/loaders/use-timeline'
import {conversationCommentsQueryKey} from '../../../page-data/loaders/use-conversation-comments'
import {useState} from 'react'

export function useArchiveActions() {
  const [confirmArchive, setConfirmArchive] = useState<boolean | null>(null)
  const url = usePageDataUrl(PageData.archive)
  const queryClient = useQueryClient()
  const invalidatePrState = useInvalidatePrState()
  const mergeBoxQueryKey = useMergeBoxQueryKey()
  const {pullRequest, user} = useLayoutQuery().data
  const {pathName} = pullRequest

  const mutation = useMutation({
    mutationFn: async (archive: boolean) => {
      const response = await fetchWithErrorHandling(url, {method: archive ? 'POST' : 'DELETE'})
      const json = await parseJSONWithBetterErrors(response)
      throwErrorsIfBadResponse(response, json)
    },
    onSuccess: () => setConfirmArchive(null),
    // A failed archive may already have closed the PR, so refresh after failures too.
    onSettled: () =>
      Promise.all([
        invalidatePrState(),
        queryClient.invalidateQueries({queryKey: mergeBoxQueryKey}),
        queryClient.invalidateQueries({queryKey: timelineQueryKey(pathName)}),
        queryClient.invalidateQueries({queryKey: conversationCommentsQueryKey(pathName)}),
      ]),
  })

  return {
    canUnarchive: Boolean(user?.canUnarchive),
    mutation,
    confirmArchive,
    setConfirmArchive,
    visible: confirmArchive !== null || Boolean(user?.canArchive || user?.canUnarchive),
  }
}
